import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import PreviewPage from "./pages/PreviewPage";
import AdminLayout from "./pages/admin/AdminLayout";
import DashboardPage from "./pages/admin/DashboardPage";
import InboxPage from "./pages/admin/InboxPage";
import MattersPage from "./pages/admin/MattersPage";
import MatterDetailPage from "./pages/admin/MatterDetailPage";
import MatterConflictLogPage from "./pages/admin/MatterConflictLogPage";
import AttorneysPage from "./pages/admin/AttorneysPage";
import BookingsPage from "./pages/admin/BookingsPage";
import DraftsPage from "./pages/admin/DraftsPage";
import DraftDetailPage from "./pages/admin/DraftDetailPage";
import DeadlinesPage from "./pages/admin/DeadlinesPage";
import IntakesPage from "./pages/admin/IntakesPage";
import ConflictCheckPage from "./pages/admin/ConflictCheckPage";
import IntegrationsPage from "./pages/admin/IntegrationsPage";
import IoltaPage from "./pages/admin/IoltaPage";
import ReviewsPage from "./pages/admin/ReviewsPage";
import ReviewsPendingPage from "./pages/admin/ReviewsPendingPage";
import SitePage from "./pages/admin/SitePage";
import SiteBuilderPage from "./pages/admin/SiteBuilderPage";
import ThemePage from "./pages/admin/ThemePage";
import SmsPage from "./pages/admin/SmsPage";
import SmsThreadPage from "./pages/admin/SmsThreadPage";
import SectionsPage from "./pages/admin/SectionsPage";
import SectionEditorPage from "./pages/admin/SectionEditorPage";
import SectionsHarnessPage from "./pages/admin/SectionsHarnessPage";
import RegistryPage from "./pages/admin/RegistryPage";
import PlaybooksPage from "./pages/admin/PlaybooksPage";
import AIActivationPage from "./pages/admin/AIActivationPage";
import AICostsPage from "./pages/admin/AICostsPage";
import AuditLogPage from "./pages/admin/AuditLogPage";
import PlatformPage from "./pages/admin/PlatformPage";
import PlatformTenantsPage from "./pages/admin/PlatformTenantsPage";
import PlatformAuditPage from "./pages/admin/PlatformAuditPage";
import PlatformObservabilityPage from "./pages/admin/PlatformObservabilityPage";
import ProfilePage from "./pages/admin/ProfilePage";
import { useAuth } from "./lib/auth";

function RequireAuth({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/preview/:tenantSlug/:locale" element={<PreviewPage />} />
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="matters" element={<MattersPage />} />
        <Route path="matters/:matterId" element={<MatterDetailPage />} />
        <Route path="matters/:matterId/conflict-log" element={<MatterConflictLogPage />} />
        <Route path="attorneys" element={<AttorneysPage />} />
        <Route path="bookings" element={<BookingsPage />} />
        <Route path="drafts" element={<DraftsPage />} />
        <Route path="drafts/:id" element={<DraftDetailPage />} />
        <Route path="deadlines" element={<DeadlinesPage />} />
        <Route path="intakes/active" element={<IntakesPage />} />
        <Route path="conflict-check" element={<ConflictCheckPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="iolta" element={<IoltaPage />} />
        <Route path="reviews" element={<ReviewsPage />} />
        <Route path="reviews/pending" element={<ReviewsPendingPage />} />
        <Route path="site" element={<SitePage />} />
        <Route path="site-builder" element={<SiteBuilderPage />} />
        <Route path="theme" element={<ThemePage />} />
        <Route path="sms" element={<SmsPage />} />
        <Route path="sms/threads/:threadId" element={<SmsThreadPage />} />
        <Route path="sections" element={<SectionsPage />} />
        <Route path="sections/:sectionTypeId" element={<SectionEditorPage />} />
        <Route path="sections-harness" element={<SectionsHarnessPage />} />
        <Route path="registry" element={<RegistryPage />} />
        <Route path="playbooks" element={<PlaybooksPage />} />
        <Route path="ai-activation" element={<AIActivationPage />} />
        <Route path="ai-costs" element={<AICostsPage />} />
        <Route path="audit" element={<AuditLogPage />} />
        <Route path="platform" element={<PlatformPage />} />
        <Route path="platform/tenants" element={<PlatformTenantsPage />} />
        <Route path="platform/audit-log" element={<PlatformAuditPage />} />
        <Route path="platform/observability" element={<PlatformObservabilityPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
